import { StoreAnimatedHeightDiv } from "@/components/StoreComponents/StoreAnimatedHeightDiv";
import { ChevronDownIcon, TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import {
  Collapse,
  styled,
  Skeleton,
  SxProps,
  Theme,
  alpha,
} from "@mui/material";
import { AnimatePresence, motion } from "motion/react";
import React from "react";

const StyledCollapsibleSection = styled("div")<{
  disabled?: boolean;
}>(({ disabled }) => ({
  overflow: "hidden",

  padding: "30px 0",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "20px 0",
  },
}));

export const SectionTitle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  gap: 16,
  ".collapsible-section-title": {
    fontSize: 20,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    color: theme.vars.palette.text.primary,
    gap: 8,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
      fontWeight: 600,
      color: alpha(theme.palette.text.primary, 0.5),
    },
  },
  ".collapsible-section-icon": {
    marginLeft: 16,
  },
}));

const Header = styled("button")<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    border: "none",
    cursor: disabled ? "default" : "pointer",
    background: "transparent",
    color: theme.vars.palette.text.primary,
    lineHeight: "32px",
    padding: 0,
    textAlign: "left",
    [theme.breakpoints.down("md")]: {
      lineHeight: "23.5px",
      minHeight: 23.5,
    },
  })
);

const Content = styled("div")(({ theme }) => ({
  paddingTop: 16,
  fontSize: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "14px",
    paddingTop: 5,
  },
}));

const CollapsedContent = styled("div")<{ disabled?: boolean }>(({ theme }) => ({
  opacity: 1,
  paddingTop: 16,
  [theme.breakpoints.up("md")]: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "24px",
    textAlign: "left",
  },
  [theme.breakpoints.down("md")]: {
    paddingTop: 10,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "18px",
    textAlign: "left",
  },
}));

const CollapsibleSection: React.FC<{
  isOpen: boolean;
  onToggle?: () => void;
  title: string | React.ReactNode;
  titleIcon?: React.ReactNode;
  className?: string;
  collapsedContent?: string | React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  hideControl?: boolean;
  style?: React.CSSProperties;
  loading?: boolean;
  sx?: SxProps<Theme>;
  constantContent?: boolean;
  smoothAnimation?: boolean;
}> = ({
  isOpen,
  onToggle,
  title,
  titleIcon,
  children,
  disabled,
  hideControl = false,
  collapsedContent,
  style,
  loading,
  className,
  sx,
  constantContent = false,
  smoothAnimation = false,
}) => {
  if (loading) {
    return (
      <StyledCollapsibleSection
        className={`collapsible-section ${className}`}
        disabled
        style={style}
        sx={sx}
      >
        <Header disabled={true}>
          <SectionTitle>
            <Skeleton
              variant="rectangular"
              width={200}
              height={24}
              sx={{ borderRadius: "4px" }}
            />
          </SectionTitle>
          {!hideControl && (
            <div>
              <ChevronDownIcon
                style={{
                  transition: "transform 0.3s",
                  width: "25px",
                  height: "25px",
                  transform: "rotate(0deg)",
                  opacity: 0.5,
                }}
              />
            </div>
          )}
        </Header>
        <Collapse in={true}>
          <CollapsedContent disabled={disabled}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={24}
              sx={{ borderRadius: "4px" }}
            />
          </CollapsedContent>
        </Collapse>
      </StyledCollapsibleSection>
    );
  }

  return (
    <StyledCollapsibleSection
      className={`collapsible-section ${className}`}
      disabled={disabled}
      style={style}
      sx={sx}
    >
      <Header disabled={disabled} onClick={onToggle}>
        <SectionTitle>
          <div className="collapsible-section-title">{title}</div>
          {titleIcon}
        </SectionTitle>
        {!hideControl && (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "25px",
              height: "25px",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        )}
      </Header>
      <StoreAnimatedHeightDiv>
        <AnimatePresence mode="wait">
          <motion.div
            layout="position"
            key={smoothAnimation ? "" : isOpen ? "view" : "edit"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.05 }}
          >
            {constantContent ? (
              <Content>{children}</Content>
            ) : isOpen ? (
              <Content>{children}</Content>
            ) : (
              collapsedContent
            )}
          </motion.div>
        </AnimatePresence>
      </StoreAnimatedHeightDiv>
    </StyledCollapsibleSection>
  );
};

export default CollapsibleSection;
